import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, signal, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import { cloneDeep } from 'lodash';
import { ToastMessageOptions } from 'primeng/api';

import { VotingResult } from '~/voting/voting.types';

@Component({
  selector: 'app-voting-result',
  templateUrl: './voting-result.component.html',
  styleUrl: './voting-result.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VotingResultComponent implements AfterViewInit {
  @ViewChild('chartCanvas', { static: false }) chartCanvas: ElementRef<HTMLCanvasElement>;
  @Input() set votingResult(result: VotingResult) {
    if (!result) {
      this.result.set(null);
      return;
    }

    this.result.set(cloneDeep(result));
    this.messages = [
      { severity: 'info', detail: `${this.translateService.instant('voting.result.title.prefix')} ${result.title}` }
    ];

    if (!result.quorum) {
      this.messages.push({
        severity: 'warn',
        detail: `${this.translateService.instant('voting.result.warning.quorum')}`
      });
    }

    if (!result.majority) {
      this.messages.push({
        severity: 'warn',
        detail: `${this.translateService.instant('voting.result.warning.majority')}`
      });
    }

    this.messages.push({
      severity: result.positiveResult ? 'success' : 'error',
      detail: result.result
    });

    if (this.chartCanvas) {
      this.createChart();
    }
  }

  public result = signal<VotingResult>(null);
  public messages: ToastMessageOptions[];
  public chart = signal<Chart>(null);

  constructor(private translateService: TranslateService) {}

  ngAfterViewInit() {
    if (this.result()) {
      this.createChart();
    }
  }

  createChart() {
    const labels: Array<string> = [];
    const VOTING_RESULT_CHART_DATA: Array<number> = [];
    const VOTING_RESULT_CHART_DATA2: Array<number> = [];
    const barThickness = 'flex';

    for (const votingResultVote of this.result().votingResultDataByType) {
      labels.push(votingResultVote.name);
      VOTING_RESULT_CHART_DATA.push(votingResultVote.count);
      if (this.result().candidateVoting && votingResultVote.count2 !== undefined) {
        VOTING_RESULT_CHART_DATA2.push(votingResultVote.count2);
      }
    }

    const dataSets = [];
    dataSets.push({
      label: this.translateService.instant('voting.result.type.for'),
      data: VOTING_RESULT_CHART_DATA,
      backgroundColor: this.result().graphBarColor,
      barThickness
    });
    if (this.result().candidateVoting && VOTING_RESULT_CHART_DATA2.length > 0) {
      dataSets.push({
        label: this.translateService.instant('voting.result.type.against'),
        data: VOTING_RESULT_CHART_DATA2,
        backgroundColor: this.result().graphBarColor2,
        barThickness
      });
    }
    if (this.chart()) {
      this.chart().destroy();
    }

    this.chart.set(
      new Chart(this.chartCanvas.nativeElement, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: dataSets
        },
        options: {
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: {
              display: this.result().candidateVoting && VOTING_RESULT_CHART_DATA2.length > 0,
              position: 'bottom'
            },
            tooltip: {
              displayColors: false
            }
          },
          scales: {
            x: {
              ticks: {
                precision: 0
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                precision: 0
              }
            }
          }
        }
      })
    );
  }
}
